/**
 * FromTheManual — "From the manual" PDP section (owner directive 07/20:
 * harvest the manuals and enrich the product pages; FRIDGE-355 08/17/2026:
 * make it robust and unique for search — it is the one block of text on a
 * product page that no other site carries).
 *
 * Data source:
 *   - canonical_products.manual_guidance jsonb, resolved server-side by
 *     lib/render-policy/display-manual-guidance getDisplayManualGuidance.
 *   - Every quote is a VERBATIM passage from the hosted manufacturer document
 *     for this exact model: each one passed a literal-substring evidence gate
 *     against the PDF's own text layer before it was banked, and a second
 *     display-quality gate in the resolver (honest extraction — nothing here
 *     is paraphrased or invented).
 *   - Every other string on screen — heading, eyebrow, intro, the question
 *     above each quote, the source line — is composed by the resolver from
 *     stored fields only (document type, brand, model, extraction date). No
 *     field, no sentence.
 *
 * Shape (why it looks like this):
 *   - <section id="from-the-manual"> so the block can be deep-linked;
 *   - one <h3> per populated field, phrased as the question a shopper types,
 *     with the verbatim quote as its answer — the same items are unioned into
 *     the page's single FAQPage JSON-LD node in page.tsx (schema equals visible
 *     by construction; this component emits NO <script> of its own);
 *   - a visible source line naming the document, the model and the extraction
 *     date, and a fridge.com-fronted link to the full document.
 *
 * Brand policy / Rule 4:
 *   - The "Read the full document" link is the fridge.com /manuals/... proxy
 *     path, fronted server-side by the resolver. No *.supabase.co host and no
 *     external manufacturer-CDN href can reach this component's output.
 *
 * Real-or-Hidden Rule 3:
 *   - null guidance → return null. Section absent from the DOM.
 */

import Link from 'next/link'
import { BookOpen, Ruler, Thermometer, Sparkles, Shield, ArrowUpRight } from 'lucide-react'
import type {
  DisplayManualGuidance,
  ManualGuidanceItem,
  ManualGuidanceKey,
} from '@/lib/render-policy/display-manual-guidance'

interface FromTheManualProps {
  guidance: DisplayManualGuidance | null | undefined
  className?: string
}

const ITEM_ICONS: Record<ManualGuidanceKey, typeof Ruler> = {
  install_clearances: Ruler,
  temperature_range: Thermometer,
  care_maintenance: Sparkles,
  warranty_terms: Shield,
}

function Quote({ text, cite }: { text: string; cite: string | null }) {
  return (
    <blockquote
      className="text-sm text-gray-600 leading-relaxed border-l-2 border-[#0469f0]/30 pl-3"
      {...(cite ? { cite } : {})}
    >
      &ldquo;{text}&rdquo;
    </blockquote>
  )
}

function GuidanceItem({ item, cite }: { item: ManualGuidanceItem; cite: string | null }) {
  const Icon = ITEM_ICONS[item.key]
  return (
    <div id={`manual-topic-${item.key}`} className="flex gap-3 scroll-mt-28" data-testid={`manual-item-${item.key}`}>
      <span className="w-9 h-9 rounded-lg bg-[#0469f0]/10 flex items-center justify-center text-[#0469f0] flex-shrink-0">
        <Icon className="w-4 h-4" aria-hidden="true" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-0.5">
          {item.label}
        </p>
        <h3 className="text-sm font-semibold text-gray-900 mb-1.5">{item.question}</h3>
        {item.quotes.length === 1 ? (
          <Quote text={item.quotes[0]} cite={cite} />
        ) : (
          <ul className="space-y-2">
            {item.quotes.map((q, i) => (
              <li key={i}>
                <Quote text={q} cite={cite} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export function FromTheManual({ guidance, className = '' }: FromTheManualProps) {
  if (!guidance || guidance.items.length === 0) return null
  const { items, manualHref, heading, eyebrow, intro, sourceLine } = guidance

  return (
    <section
      id="from-the-manual"
      className={`rounded-lg border border-gray-200 bg-white p-4 sm:p-5 scroll-mt-28 ${className}`}
      aria-labelledby="from-the-manual-heading"
      data-testid="from-the-manual"
      data-manual-item-count={items.length}
    >
      <header className="mb-3">
        <p className="text-[#ff9951] font-bold tracking-widest text-[11px] uppercase mb-1.5">
          {eyebrow}
        </p>
        <h2
          id="from-the-manual-heading"
          className="text-xl md:text-2xl font-black tracking-tighter text-gray-900"
        >
          {heading}
        </h2>
      </header>
      <p className="text-sm text-gray-500 leading-relaxed mb-4">{intro}</p>

      {items.length > 1 && (
        <nav aria-label="Available manual topics" className="mb-5 rounded-lg bg-gray-50 p-3">
          <p className="text-sm font-semibold text-gray-900 mb-2">In these manual excerpts</p>
          <ul className="flex flex-wrap gap-x-3 gap-y-1">
            {items.map((item) => (
              <li key={item.key}>
                <a
                  href={`#manual-topic-${item.key}`}
                  className="inline-flex min-h-11 items-center text-sm font-semibold text-[#0469f0] underline underline-offset-4 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40"
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <div className="space-y-5">
        {items.map((item) => (
          <GuidanceItem key={item.key} item={item} cite={manualHref} />
        ))}
      </div>

      <footer className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-xs text-gray-500" data-testid="from-the-manual-source">
          {sourceLine}
        </p>
        {manualHref && (
          <Link
            href={manualHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0469f0] hover:text-[#0353c0] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40 rounded flex-shrink-0"
          >
            <BookOpen className="w-4 h-4" aria-hidden="true" />
            Read the full document
            <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
          </Link>
        )}
      </footer>
    </section>
  )
}

export default FromTheManual
